import { ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToolPreconditions } from '@/hooks';
import { cn } from '@/lib/utils';

interface IntegrationActionCardProps {
	provider: string;
	tool: string;
	label: string;
	description: string;
	riskLevel: string;
	onSelect: () => void;
}

/** One action row; actions guarded by enabled preconditions get an accent and a count badge. */
export const IntegrationActionCard = ({
	provider,
	tool,
	label,
	description,
	riskLevel,
	onSelect
}: IntegrationActionCardProps) => {
	const { data: preconditions } = useToolPreconditions(provider, tool);
	const activeCount = (preconditions ?? []).filter((rule) => rule.enabled).length;
	const hasPreconditions = activeCount > 0;

	return (
		<button
			onClick={onSelect}
			className={cn(
				'text-left flex flex-col gap-1 border rounded-xl p-3 transition-colors',
				hasPreconditions
					? 'border-indigo-500/40 bg-indigo-500/5 hover:bg-indigo-500/10'
					: 'border-gray-300 hover:bg-gray-50'
			)}
		>
			<div className='flex items-center justify-between gap-2'>
				<span className='text-sm font-medium text-ink'>{label}</span>
				<div className='flex items-center gap-1.5 shrink-0'>
					{hasPreconditions && (
						<Badge variant='outline' className='gap-1 border-indigo-500/40 text-indigo-500'>
							<ShieldCheck size={12} />
							{activeCount} {activeCount === 1 ? 'precondition' : 'preconditions'}
						</Badge>
					)}
					<Badge variant={riskLevel === 'high' ? 'warning' : 'secondary'} className='capitalize'>
						{riskLevel} risk
					</Badge>
				</div>
			</div>
			<p className='text-[13px] leading-[18px] text-muted-foreground'>{description}</p>
		</button>
	);
};
